// BestCaregiversSection.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Minus, ArrowRight } from 'lucide-react';
import careImage from '../assets/care-section-image.jpg';

const BestCaregiversSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const careServices = [
    {
      name: "Alzheimer's and Dementia Care",
      slug: 'alzheimers-dementia-care',
      title: 'Compassionate in-home care for people living with dementia',
      description: "We sit down with you to shape a dementia care plan that suits your family, from a few hours a week through to continuous, around-the-clock support. Our carers bring the patience, empathy, and steadiness that help your loved one stay safe and settled in their own home."
    },
    {
      name: 'Companionship',
      slug: 'companionship-care',
      title: 'Companionship that eases loneliness',
      description: "We all need the company of others. Time alone can be hard for older people, so a warm, attentive carer offers the conversation, shared activities, and friendship that make the days brighter."
    },
    {
      name: 'Bathing, Grooming, and Dressing',
      slug: 'personal-care',
      title: 'Dignified personal care at home',
      description: "Everyday routines like bathing, dressing, and grooming can become difficult with age. We help your loved one keep their independence and dignity at home, providing reliable, discreet personal care that is always delivered with respect."
    },
    {
      name: 'Hospice Care',
      slug: 'hospice-care',
      title: 'Comfort-focused hospice support',
      description: "For people living with a life-limiting condition, our carers focus on comfort, dignity, and quality of life. We assist with daily needs and offer steady emotional support, so time can be spent at home, surrounded by care."
    },
    {
      name: 'Specialty Care',
      slug: 'specialty-care',
      title: 'Specialised support for complex needs',
      description: "For people with unique health needs or ongoing conditions, we provide focused, personalised support. From complex daily routines to specialised equipment, our aim is to help each person stay comfortable, safe, and as independent as possible."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top intro */}
        <div className="max-w-4xl mb-16">
          <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Our Carers
          </p>
          <h2 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl lg:text-5xl mb-6">
            We Provide Only the Best Carers for Your Loved One
          </h2>
          <div className="space-y-4 text-muted text-base leading-relaxed">
            <p>
              Every carer we send is experienced, thoroughly trained, fully insured, and has completed
              comprehensive background checks. We only introduce people we would trust in our own homes,
              and we thoughtfully match each carer to a client based on their needs and preferences. For
              example, we would not pair a chatty personality with someone who values quiet.
            </p>
            <p>
              Whether it is help with daily mobility or a companion for games and conversation, our
              tailored in-home care helps your loved one thrive at home, even when you are away. It is
              the kind of support that brings reassurance to families and comfort to those in our care.
            </p>
          </div>
        </div>

        {/* Image + accordion */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div>
            <img
              src={careImage}
              alt="A carer embracing an older woman"
              className="w-full h-[420px] lg:h-[560px] object-cover rounded-2xl"
            />
          </div>

          {/* Accordion */}
          <div>
            <h3 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl mb-4">
              The Right Care for Every Need
            </h3>
            <p className="text-muted text-base leading-relaxed mb-10 max-w-md">
              From companionship to personal care, the carers we match provide support shaped to every
              situation.
            </p>

            <div>
              {careServices.map((service, index) => {
                const open = openIndex === index;
                return (
                  <div key={service.slug} className="border-b border-line">
                    <button
                      onClick={() => setOpenIndex(open ? null : index)}
                      className="w-full flex items-center gap-3 text-left py-5"
                    >
                      <span className="text-primary shrink-0">
                        {open ? <Minus size={20} /> : <Plus size={20} />}
                      </span>
                      <span className={`text-lg ${open ? 'text-primary' : 'text-ink'} font-medium transition-colors`}>
                        {service.name}
                      </span>
                    </button>

                    {open && (
                      <div className="pb-6 pl-8 pr-2 space-y-3">
                        {service.title && (
                          <h4 className="text-ink font-medium">{service.title}</h4>
                        )}
                        <p className="text-muted text-sm leading-relaxed whitespace-pre-line">
                          {service.description}
                        </p>
                        <Link
                          to={`/services/${service.slug}`}
                          className="inline-flex items-center gap-1.5 text-primary hover:text-primary-dark font-medium text-sm transition-colors"
                        >
                          Learn more
                          <ArrowRight size={15} />
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BestCaregiversSection;
